import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d9m0gtobdkpk99j7g5fg';

export default function ContactForm() {
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLDivElement>();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      // Bot detected — show fake success
      setStatus('success');
      form.reset();
      setCharCount(0);
      return;
    }

    // Remove honeypot from payload
    formData.delete('website_alt');

    // Build URL-encoded payload
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Not JSON
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;

      if (!response.ok) {
        setStatus('error');
        setErrorMsg(serverMsg || 'Error al enviar. Intenta de nuevo.');
        return;
      }

      if (parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
        setStatus('error');
        setErrorMsg(serverMsg);
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || 'Error al enviar. Intenta de nuevo.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Error de conexión. Intenta de nuevo.');
    }
  };

  return (
    <section id="contacto" className="relative py-20 md:py-28 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div ref={sectionRef} className="max-w-4xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-800 text-sm font-semibold mb-4">
              Empecemos
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-900">
              Solicita tu demo gratuita
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
              Contanos sobre tu cooperativa de agua y te contactamos para una demostración personalizada sin compromiso.
            </p>
          </div>

          {/* Form */}
          <div className={`bg-background-50 rounded-2xl border border-background-200 p-6 md:p-10 shadow-sm transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <form
              id="demo-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Tu nombre"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="entity" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Cooperativa o entidad
                </label>
                <input
                  type="text"
                  id="entity"
                  name="entity"
                  required
                  placeholder="Nombre de tu cooperativa de agua"
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Mensaje
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Contanos cuántos afiliados tenés, qué problemas querés resolver..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent transition-all resize-none"
                />
                <div className="flex justify-end mt-1">
                  <span className={`text-xs ${charCount > 450 ? 'text-accent-600' : 'text-foreground-400'}`}>
                    {charCount}/500
                  </span>
                </div>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 text-white text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Enviar solicitud
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <div className="flex items-center gap-2 text-accent-700 bg-accent-50 px-4 py-2.5 rounded-lg text-sm">
                    <i className="ri-check-line text-lg" />
                    <span>¡Mensaje enviado! Te contactamos pronto.</span>
                  </div>
                )}

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-700 bg-red-50 px-4 py-2.5 rounded-lg text-sm">
                    <i className="ri-error-warning-line text-lg" />
                    <span>{errorMsg || 'Error al enviar. Intenta de nuevo.'}</span>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}